import { TDocumentConfig } from '../types';

// =============================================================================
// LAYOUT CONSTANTS
// =============================================================================
// A4 paper dimensions used for document layout.
//
// These values must stay in sync with:
//   - content-section.scss: $portrait-paper, $landscape-paper, $horizontal-padding
//   - ItemPage.tsx: CONTENT_WIDTH_PX values (794px ≈ 210mm, 1123px ≈ 297mm at 96 DPI)
//
// Width flow:
//   ItemPage (maxWidth) > Content (containerWidth) > Paper (paperWidth)
// =============================================================================

/** A4 portrait paper width */
export const PORTRAIT_PAPER_WIDTH = '210mm';

/** A4 landscape paper width */
export const LANDSCAPE_PAPER_WIDTH = '297mm';

/** Horizontal padding inside the paper container */
export const HORIZONTAL_PADDING = '18mm';

/** Sidebar width for comments, impact analysis, etc. */
export const SIDEBAR_WIDTH = '300px';

/** Centering offset when portrait paper is centered in landscape container */
export const CENTERING_OFFSET = `calc((${LANDSCAPE_PAPER_WIDTH} - ${PORTRAIT_PAPER_WIDTH}) / 2)`; // 43.5mm

// Pixel equivalents at 96 DPI (standard screen resolution)
// Used by ItemPage.tsx for maxWidth calculations
const MM_TO_PX_96DPI = 96 / 25.4; // ~3.78 px per mm
export const PORTRAIT_PAPER_WIDTH_PX = Math.round(210 * MM_TO_PX_96DPI); // 794px
export const LANDSCAPE_PAPER_WIDTH_PX = Math.round(297 * MM_TO_PX_96DPI); // 1123px

/** Sidebar width in pixels (matches SIDEBAR_WIDTH but in px for calculations) */
export const SIDEBAR_WIDTH_PX = 300;

/** Container horizontal padding in pixels (px-4 = 16px × 2) */
export const CONTAINER_PADDING_PX = 32;

// =============================================================================

/**
 * Get document layout configuration based on orientation.
 *
 * Layout model:
 * - Portrait mode with sections: 210mm paper centered in 297mm container (allows landscape sections to extend)
 * - Portrait mode without sections: 210mm paper in 210mm container (narrower, easier to read)
 * - Landscape mode: 297mm paper left-aligned in 297mm container
 *
 * Auto-resize: When a portrait document has no landscape sections, the container
 * shrinks to 210mm for a tighter layout. Adding sections auto-expands to 297mm.
 */
export const getDocumentConfig = (
  config?: TDocumentConfig,
  opts?: { isSpreadsheet?: boolean; hasLandscapeSections?: boolean }
) => {
  const landscape = Boolean(config?.landscape);
  const hasSections = opts?.hasLandscapeSections ?? false;

  // Paper: A4 portrait (210mm) or landscape (297mm)
  const paperWidth = landscape ? LANDSCAPE_PAPER_WIDTH : PORTRAIT_PAPER_WIDTH;

  // Container: 297mm if landscape mode OR has sections, otherwise just portrait width
  const containerWidth = landscape || hasSections ? LANDSCAPE_PAPER_WIDTH : PORTRAIT_PAPER_WIDTH;

  // Portrait: paper centered when container is wide. Landscape: paper left-aligned
  const shouldCenterPaper = !landscape && hasSections;

  // Centering margin for portrait mode with sections ('0' for landscape or narrow container)
  const centeringMargin = shouldCenterPaper ? CENTERING_OFFSET : '0px';

  let contentWidth = paperWidth;
  if (opts?.isSpreadsheet) contentWidth = '100%';

  return {
    headingStart: config?.headingStart || 1,
    landscape,
    paperWidth,
    containerWidth,
    contentWidth,
    shouldCenterPaper,
    centeringMargin,
    uiOrientation: landscape ? ['Landscape'] : ['Portrait'],
  };
};

export default getDocumentConfig;
